import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingCart } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export default function PublicNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { getItemCount, openCart } = useCart();
  const navigate = useNavigate();
  
  const cartCount = getItemCount();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 text-white">
      <div className="relative bg-gradient-to-r from-blue-600/80 via-blue-500/70 to-blue-600/80">
        <div className="relative mx-auto flex max-w-7xl items-center gap-3 lg:gap-8 px-6 py-3 text-[11px] font-semibold uppercase">
          {/* Mobile Header */}
          <div className="md:hidden flex w-full items-center justify-between">
            <button
              className="inline-flex items-center p-2 rounded-md hover:bg-white/10 transition"
              aria-label="Open menu"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <Link to="/" className="flex items-center gap-3 shrink-0">
              <img
                src="/assets/logo.png"
                alt="CamSur Pickleball Club"
                className="h-10 w-auto"
              />
            </Link>
            
            {/* Mobile Cart Icon */}
            {user && (
              <button
                onClick={openCart}
                className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-6 w-6 text-white" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#c5e153] text-[#004a80] text-xs font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Desktop */}
          <div className="hidden md:flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3 shrink-0">
                <img
                  src="/assets/logo.png"
                  alt="CamSur Pickleball Club"
                  className="h-10 w-auto"
                />
              </Link>

              <button
                className="inline-flex items-center p-2 rounded-md hover:bg-white/10 transition"
                aria-label="Open menu"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                <svg
                  className="h-7 w-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex flex-1 items-center justify-center gap-x-6">
              <button onClick={() => scrollToSection('home')} className="hover:opacity-80">HOME</button>
              <button onClick={() => scrollToSection('whats-new')} className="hover:opacity-80">WHAT'S NEW</button>
              <button onClick={() => scrollToSection('gallery')} className="hover:opacity-80">GALLERY</button>
              <button onClick={() => scrollToSection('facility')} className="hover:opacity-80">FACILITY</button>
              <button onClick={() => scrollToSection('rates')} className="hover:opacity-80">RATES</button>
              <button onClick={() => scrollToSection('addons')} className="hover:opacity-80">SHOP</button>
              <button onClick={() => scrollToSection('court-rules')} className="hover:opacity-80">COURT RULES</button>
              <button onClick={() => scrollToSection('location')} className="hover:opacity-80">LOCATION</button>
            </div>

            {/* Auth Section */}
            <div className="flex items-center gap-3">
              {/* Cart Icon */}
              {user && (
                <button
                  onClick={openCart}
                  className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Open cart"
                >
                  <ShoppingCart className="h-6 w-6 text-white" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#c5e153] text-[#004a80] text-xs font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </button>
              )}
              
              {!user ? (
                <Link to="/login" className="hover:underline font-bold tracking-wider">LOG IN / SIGN UP</Link>
              ) : (
                <DropdownMenu className="">
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center space-x-2 px-2 hover:bg-white/10 rounded-2xl"
                    >
                      <Avatar className="h-8 w-8 bg-[#FF8000AD]">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback>
                          {user?.name?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">
                        {user?.name?.split(" ")[0]}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => navigate("/profile")}>Profile</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/my-bookings")}>My Bookings</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-destructive">Log out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="bg-[#16A0FF] px-6 pb-4 relative border-t border-white/10">
            <button
              className="absolute top-3 right-3 p-2 rounded-md hover:bg-white/20 transition bg-white/10 border border-white/20"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex flex-col gap-3 pt-4">
              <button onClick={() => { scrollToSection('home'); setMobileOpen(false); }} className="hover:opacity-80 text-left">HOME</button>
              <button onClick={() => { scrollToSection('whats-new'); setMobileOpen(false); }} className="hover:opacity-80 text-left">WHAT'S NEW</button>
              <button onClick={() => { scrollToSection('whats-new'); setMobileOpen(false); }} className="hover:opacity-80 text-left">GALLERY</button>
              <button onClick={() => { scrollToSection('facility'); setMobileOpen(false); }} className="hover:opacity-80 text-left">FACILITY</button>
              <button onClick={() => { scrollToSection('rates'); setMobileOpen(false); }} className="hover:opacity-80 text-left">RATES</button>
              <button onClick={() => { scrollToSection('addons'); setMobileOpen(false); }} className="hover:opacity-80 text-left">SHOP</button>
              <button onClick={() => { scrollToSection('court-rules'); setMobileOpen(false); }} className="hover:opacity-80 text-left">COURT RULES</button>
              <button onClick={() => { scrollToSection('location'); setMobileOpen(false); }} className="hover:opacity-80 text-left">LOCATION</button>

              <div className="pt-2 border-t border-white/20">
                {!user ? (
                  <Link to="/login" className="hover:underline font-bold tracking-wider">LOG IN / SIGN UP</Link>
                ) : (
                  <button
                    onClick={() => {
                      logout();
                      setMobileOpen(false);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    Log out
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
